import { useState } from "react";
import { AlertOctagon, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import JsonBlock from "../leads/conversation/JsonBlock";
import { trpc } from "../../trpc";
import type { WebhookCatalogEntry, WebhookEventState, WebhookEventType } from "./types";

type WebhookTestPanelProps = {
  catalog: WebhookCatalogEntry[];
  events: WebhookEventState[];
  disabledReason: string | null;
};

const DEFAULT_TEST_EVENT: WebhookEventType = "lead.status-updated";

const parseResponseBody = (body: string): unknown => {
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

const WebhookTestPanel = ({
  catalog,
  events,
  disabledReason,
}: WebhookTestPanelProps): JSX.Element => {
  const [event, setEvent] = useState<WebhookEventType>(
    () => events.find((candidate) => candidate.enabled)?.type ?? DEFAULT_TEST_EVENT
  );
  const sendTest = trpc.webhooks.sendTest.useMutation();

  const handleEventChange = (value: string) => {
    setEvent(value as WebhookEventType);
    sendTest.reset();
  };

  const result = sendTest.data;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6">
      <div>
        <h3 className="text-base font-semibold">Enviar un evento de prueba</h3>
        <p className="text-sm text-gray-600">
          Enviamos el ejemplo del evento a tu URL, con tu secreto y el encabezado{" "}
          <code className="text-xs">X-Palomma-Test: true</code> para que lo distingas de uno real.
          No crea ni cambia ningún lead.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex flex-col gap-2 md:w-72">
          <Label htmlFor="webhook-test-event">Evento</Label>
          <Select value={event} onValueChange={handleEventChange}>
            <SelectTrigger id="webhook-test-event" className="min-h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {catalog.map((entry) => (
                <SelectItem key={entry.type} value={entry.type}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 w-full md:w-auto"
          disabled={disabledReason !== null || sendTest.isLoading}
          onClick={() => sendTest.mutate({ event })}
        >
          <Send />
          {sendTest.isLoading ? "Enviando…" : "Enviar evento de prueba"}
        </Button>
      </div>

      {disabledReason && <p className="text-xs text-gray-600">{disabledReason}</p>}

      <div aria-live="polite">
        {sendTest.error && (
          <p className="flex items-start gap-1 text-xs text-red-600">
            <AlertOctagon className="mt-px size-3.5 shrink-0" />
            {sendTest.error.message}
          </p>
        )}

        {result && (
          <div className="flex flex-col gap-3 border-t border-gray-200 pt-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <span
                  className={cn("size-2 rounded-full", result.ok ? "bg-green-600" : "bg-red-500")}
                />
                {result.ok ? "Entregado" : "Falló"}
              </span>
              {result.status !== null && (
                <span className="text-gray-600">HTTP {result.status}</span>
              )}
              <span className="text-gray-600">{result.durationMs} ms</span>
            </div>
            <p className="text-sm">{result.message}</p>
            {result.responseBody && (
              <JsonBlock label="Respuesta" value={parseResponseBody(result.responseBody)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookTestPanel;
